import {GameLogState, PlayerColor, GameState, NestedPartial} from '@colonist/game-states'
import {SocketActionController} from '../socket/SocketActionController'
import {BuildGameConfigurations, ReplayInitiationData} from '@colonist/game-base-types'
import {filterGameStateForPerspectives, filterGameLogsForPerspectives, diffDataObjects, applyDiffs, squashDiffs} from '@colonist/game-state-types'
import {UIGameEndController} from '../ui_game/ui_game_end_screen/UIGameEndController'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {KeyBindController, ShortcutLocation} from '../KeyBindController'
import {UIGameManager} from '../ui_game/UIGameManager'
import {uiGameManager} from '../EntryShared'
import {MathUtils} from '../../../../shared/utils/MathUtils'
import {ReplayAccessMethod} from '../../../../shared/entity_datas/EntityDatas'
import {getReplayAccessMethod} from './ReplayUrlUtilities'
import {setGlobalFocusLogIndex} from '@colonist/ui-game'

export class UIReplayController {
    readonly initialStateIndex = -1
    readonly replayAccessMethod: ReplayAccessMethod

    private readonly socketActionController: SocketActionController
    private readonly uiGameManager: UIGameManager
    private readonly isLiveGame: boolean
    readonly replayData: ReplayInitiationData

    readonly actionIndexForEndgame: number
    private readonly reverseGameStateChanges: NestedPartial<GameState>[]

    private actionIndex: number
    private buildGameConfigurations: BuildGameConfigurations
    private endGameScreen: UIGameEndController | undefined

    constructor(replayInitiationData: ReplayInitiationData, socketActionController: SocketActionController, isLiveGame: boolean, startingActionIndex?: number) {
        this.socketActionController = socketActionController
        this.uiGameManager = uiGameManager
        this.isLiveGame = isLiveGame
        this.replayData = replayInitiationData
        this.replayAccessMethod = getReplayAccessMethod(window.location.href) // TODO fix this, incorrect logic for mobile

        const currentAction = startingActionIndex ?? this.initialStateIndex

        this.filterGameHistory()
        this.actionIndexForEndgame = this.replayData.eventHistory.events.length - 1
        this.reverseGameStateChanges = this.getReverseGameStateChanges()

        this.actionIndex = this.initialStateIndex

        this.buildGameConfigurations = this.getBuildGameConfigurations(this.replayData.playerPerspective)
        this.uiGameManager.canvasController.buildGame()

        if(this.isLiveGame) {
            this.buildGameConfigurations.gameState.gameChatState = this.uiGameManager.gameController['stateController']['state'].gameChatState
        } else {
            this.uiGameManager.buildGameFeed()
        }

        this.socketActionController.onBuildGame(this.buildGameConfigurations)
        this.socketActionController.uiGameManager?.buildReplayView(this)
        this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.ReplayModeStart)

        this.uiGameManager.chatController?.registerGameLogClickListener((gameLogIndex: number) => {
            this.clickedGameLogWithIndex(gameLogIndex)
        })

        this.updateGameLogPosition('instant')
        this.setAction(currentAction, 'instant', false)
        this.bindKeys()
    }

    private bindKeys() {
        const keyBindDelayInMS = 0
        const withoutShift = false
        KeyBindController.bindKey('ArrowLeft', () => this.previousAction(), () => true, ShortcutLocation.InGame, withoutShift, keyBindDelayInMS)
        KeyBindController.bindKey('ArrowRight', () => this.nextAction(), () => true, ShortcutLocation.InGame, withoutShift, keyBindDelayInMS)

        const withShift = true
        KeyBindController.bindKey('ArrowLeft', () => this.previousTurn(), () => true, ShortcutLocation.InGame, withShift, keyBindDelayInMS)
        KeyBindController.bindKey('ArrowRight', () => this.nextTurn(), () => true, ShortcutLocation.InGame, withShift, keyBindDelayInMS)
    }

    private filterGameHistory(): void {
        for(const event of this.replayData.eventHistory.events) {
            if(event.stateChange.tradeState != undefined) {
                // eslint-disable-next-line
                // @ts-ignore
                delete event.stateChange.tradeState
            }

            // Filter out people toggling special build phase. Only show when they enter
            if(event.stateChange.mechanicSpecialBuildPhaseState?.playerStates != undefined) {
                delete event.stateChange.mechanicSpecialBuildPhaseState.playerStates
                if(Object.keys(event.stateChange.mechanicSpecialBuildPhaseState).length == 0) delete event.stateChange.mechanicSpecialBuildPhaseState
            }

            // Ignore chat msgs on the end game screen on a live game so players can still communicate
            if(this.isLiveGame) {
                if(event.stateChange.gameChatState != undefined) {
                    delete event.stateChange.gameChatState
                }
            }
        }

        this.replayData.eventHistory.events = this.replayData.eventHistory.events.filter((event) => Object.keys(event.stateChange).length !== 0)
    }

    private getReverseGameStateChanges(): NestedPartial<GameState>[] {
        const reverseGameEvents: NestedPartial<GameState>[] = []
        const currentState = structuredClone(this.replayData.eventHistory.initialState)
        const futureState = structuredClone(this.replayData.eventHistory.initialState)
        for(const [actionIndex, event] of this.replayData.eventHistory.events.entries()) {
            const forwardStateChange = this.replayData.eventHistory.events[actionIndex].stateChange
            applyDiffs(futureState, [forwardStateChange])

            const reverseGameStateChange = diffDataObjects(futureState, currentState)
            if(reverseGameStateChange == undefined) throw new Error('Error loading replay data')

            reverseGameEvents[actionIndex - 1] = reverseGameStateChange

            applyDiffs(currentState, [forwardStateChange])
        }

        return reverseGameEvents
    }

    private getBuildGameConfigurations(myColor: PlayerColor): BuildGameConfigurations {
        const gameState = structuredClone(this.replayData.eventHistory.initialState)
        gameState.gameLogState = this.getFullGameLogForPlayer(myColor)

        this.buildGameConfigurations = {
            gameState: gameState,
            playOrder: this.replayData.playOrder,
            playerColor: myColor,
            playerUserStates: this.replayData.playerUserStates,
            gameDetails: this.replayData.gameDetails,
            gameSettings: this.replayData.gameSettings,
            timeLeftInState: 0,
            isReplay: true,
        }
        return this.buildGameConfigurations
    }

    get playerPerspective(): PlayerColor {
        return this.buildGameConfigurations.playerColor
    }

    isPlayerPerspectiveAvailable(playerColor: PlayerColor) {
        return this.replayData.eventHistory.eventsPerspectives.includes(playerColor)
    }

    changePlayerPerspective(playerColor: PlayerColor) {
        this.uiGameManager.resetGameUIToRebuildOnReconnect()

        this.buildGameConfigurations.playerColor = playerColor

        const newPlayerGameState = structuredClone(this.replayData.eventHistory.initialState)
        for(let currentActionIndex = 0; currentActionIndex <= this.actionIndex; currentActionIndex++) {
            const singleStateDiff = this.replayData.eventHistory.events[currentActionIndex].stateChange
            const filteredStateDiff = filterGameStateForPerspectives(singleStateDiff, [this.playerPerspective])
            applyDiffs(newPlayerGameState, [filteredStateDiff])
        }

        this.buildGameConfigurations.gameState = {
            ...newPlayerGameState,
            gameLogState: this.getFullGameLogForPlayer(this.playerPerspective),
            gameChatState: this.buildGameConfigurations.gameState.gameChatState,
        }

        this.socketActionController.onBuildGame(this.buildGameConfigurations)
        this.updateGameLogPosition('instant')
        this.resetEndGameScreen()
        this.createEndGameScreen()
        if(this.actionIndex == this.actionIndexForEndgame) this.showEndGameScreen()
        else this.hideEndGameScreen()
    }

    previousAction() {
        this.setAction(this.actionIndex - 1, undefined, false)
    }

    nextAction() {
        this.setAction(this.actionIndex + 1, undefined, true)
    }

    previousTurn() {
        this.setAction(this.findPreviousTurn(), undefined, false)
    }

    nextTurn() {
        this.setAction(this.findNextTurn(), undefined, false)
    }

    getCurrentAction(): number {
        return this.actionIndex
    }

    private findNextTurn(): number {
        for(let i = this.actionIndex + 1; i < this.replayData.eventHistory.events.length; i++) {
            if((this.replayData.eventHistory.events[i].stateChange.currentState?.completedTurns) != null) return i
        }
        return this.replayData.eventHistory.events.length - 1
    }

    private findPreviousTurn(): number {
        for(let i = this.actionIndex - 1; i >= 0; i--) {
            if((this.replayData.eventHistory.events[i].stateChange.currentState?.completedTurns) != null) return i
        }
        return this.initialStateIndex
    }

    setAction(actionIndex: number, logScrollBehavior: ScrollBehavior = 'smooth', playAnimations: boolean) {
        actionIndex = MathUtils.clamp(actionIndex, this.initialStateIndex, this.actionIndexForEndgame)
        if(actionIndex == this.actionIndex) return

        actionIndex > this.actionIndex ? this.advanceToAction(actionIndex, playAnimations) : this.rewindToAction(actionIndex, playAnimations)
        this.actionIndex = actionIndex
        this.uiGameManager.replayView?.updateActionIndex(actionIndex)
        this.updateGameLogPosition(logScrollBehavior)
        this.actionIndex == this.actionIndexForEndgame ? this.showEndGameScreen() : this.hideEndGameScreen()
    }

    private advanceToAction(actionIndex: number, playAnimations: boolean) {
        const forwardDiffs: NestedPartial<GameState>[] = []
        for(let currentActionIndex = this.actionIndex + 1; currentActionIndex <= actionIndex; currentActionIndex++) {
            const stateDiff = this.replayData.eventHistory.events[currentActionIndex].stateChange
            const filteredStateDiff = filterGameStateForPerspectives(stateDiff, [this.playerPerspective])
            forwardDiffs.push(filteredStateDiff)
        }
        const squashForwardDiff = squashDiffs(forwardDiffs)
        delete squashForwardDiff.gameLogState

        this.socketActionController.gameStateUpdated({diff: squashForwardDiff, timeLeftInState: 0}, playAnimations)
    }

    private rewindToAction(actionIndex: number, playAnimations: boolean) {
        const rewindDiffs: NestedPartial<GameState>[] = []
        for(let currentActionIndex = this.actionIndex - 1; currentActionIndex >= actionIndex; currentActionIndex--) {
            const stateDiff = this.reverseGameStateChanges[currentActionIndex]
            const filteredStateDiff = filterGameStateForPerspectives(stateDiff, [this.playerPerspective])
            rewindDiffs.push(filteredStateDiff)
        }
        const squashRewindDiff = squashDiffs(rewindDiffs)
        delete squashRewindDiff.gameLogState

        this.socketActionController.gameStateUpdated({diff: squashRewindDiff, timeLeftInState: 0}, playAnimations)
    }

    private showEndGameScreen() {
        this.uiGameManager.replayView?.setVisibility(false)
        this.uiGameManager.mapController.setInteractionEnabled(false)

        if(this.endGameScreen != undefined) {
            this.endGameScreen.setVisible(true)
            this.endGameScreen?.itemBoxController?.setItemBoxVisible(true)
            this.endGameScreen?.returnToMapButton.updateButton()
            return
        }

        this.createEndGameScreen()
    }

    createEndGameScreen() {
        if(this.replayData.eventHistory.endGameState == undefined) return

        this.endGameScreen = this.socketActionController.onGameEndState({
            endGameState: this.replayData.eventHistory.endGameState,
            isReplayAvailable: true,
            rankedUserStates: this.replayData.rankedUserStates,
        }, this.isLiveGame)
    }

    hideEndGameScreen() {
        if(this.endGameScreen == undefined) return

        this.endGameScreen.setVisible(false)
        this.endGameScreen.returnToMapButton.updateButton()
        this.uiGameManager.replayView?.setVisibility(true)
        this.uiGameManager.mapController.setInteractionEnabled(true)
        this.endGameScreen.itemBoxController?.setItemBoxVisible(false)
    }

    private resetEndGameScreen() {
        if(this.endGameScreen == undefined) return

        this.endGameScreen.destroy()
        this.endGameScreen = undefined
    }

    destroy() {
        KeyBindController.instance.clearAllKeyBinds()
        this.uiGameManager.chatController?.clearGameLogClickListeners()

        this.resetEndGameScreen()

        this.uiGameManager.replayView?.setVisibility(false)
        this.uiGameManager.mapController.setInteractionEnabled(true)
    }

    clickedGameLogWithIndex(gameLogIndex: number) {
        const actionIndex = this.getActionIndexByGameLogIndex(gameLogIndex)
        if(actionIndex == undefined) return
        this.setAction(actionIndex, undefined, false)
    }

    private getActionIndexByGameLogIndex(gameLogIndex: number): number | undefined {
        if(gameLogIndex == 0) return this.initialStateIndex
        for(let actionIndex = 0; actionIndex < this.replayData.eventHistory.events.length; actionIndex++) {
            const gameLogState = this.replayData.eventHistory.events[actionIndex].stateChange.gameLogState
            for(const key in gameLogState) {
                const index = Number(key)
                if(index == gameLogIndex) return actionIndex
            }
        }
        return undefined
    }

    private getLastGameLogIndexByActionIndex(): number | undefined {
        const actionIndex = this.actionIndex
        if(actionIndex == this.initialStateIndex) return 0
        if(actionIndex < 0) return undefined

        const gameLogState = structuredClone(this.replayData.eventHistory.events[actionIndex].stateChange.gameLogState)
        if(gameLogState == undefined) return

        filterGameLogsForPerspectives(gameLogState, [this.buildGameConfigurations.playerColor])

        const actionIndices = Object.keys(gameLogState)
        if(actionIndices.length == 0) return undefined

        return Number(actionIndices[actionIndices.length - 1])
    }

    private getFullGameLogForPlayer(player: PlayerColor): GameLogState {
        const gameLogState: GameLogState = structuredClone(this.replayData.eventHistory.initialState.gameLogState)
        for(const event of this.replayData.eventHistory.events) {
            const gameLogData = event.stateChange.gameLogState

            if(gameLogData == undefined) continue

            const gameLogDataClone = structuredClone(gameLogData)

            filterGameLogsForPerspectives(gameLogDataClone, [player])

            Object.assign(gameLogState, gameLogDataClone)
        }

        return gameLogState
    }

    private updateGameLogPosition(behavior: ScrollBehavior) {
        const gameLogIndex = this.getLastGameLogIndexByActionIndex()
        if(gameLogIndex == undefined) return
        this.uiGameManager.chatController?.scrollToGameLogIndex(gameLogIndex, behavior)
        this.uiGameManager.chatController?.fadeGameLogsWithIndexGreaterThan(gameLogIndex)
        setGlobalFocusLogIndex(gameLogIndex)
    }
}
